import { useLayoutEffect, useRef } from "react";

import type { EventFilterScope } from "@/features/events/types/event-filter-scope";

import {
	useScopedEventsDispatch,
	useScopedEventsState,
} from "@/context/event-filters-registry";
import { hydrateEventsFilters } from "@/context/events";
import {
	getLocationZoneSensorIdSets,
	isPreReconcileLocationZoneSensorPlaceholder,
	type PreviousLocationTree,
	reconcileLocationZoneSensorFilters,
	sameIdSet,
} from "@/features/events/utils/filter-valid-ids";
import { readStoredEventFilters } from "@/features/events/utils/stored-events-filters";
import { useOrgLocationTree } from "@/hooks/useOrgLocationTree";
import { useAuth } from "@/providers/auth-provider";

export type ValidateLocationZoneSensorFiltersOptions = {
	enabled?: boolean;
	scope: EventFilterScope;
};

/** Normalizes L/Z/S filters against the org location tree for one page scope. */
export function useValidateLocationZoneSensorFilters({
	enabled = true,
	scope,
}: ValidateLocationZoneSensorFiltersOptions): void {
	const { orgCode } = useAuth();
	const { isLoadingLocations, locations } = useOrgLocationTree();
	const { locationsFilter, sensorsFilter, zonesFilter } =
		useScopedEventsState(scope);
	const dispatch = useScopedEventsDispatch(scope);
	// Tree from the last reconcile, so "everything selected" can grow with the org.
	const previousTreeRef = useRef<PreviousLocationTree | undefined>(undefined);

	useLayoutEffect(() => {
		if (!enabled || isLoadingLocations) return;

		const filterIds = {
			locationsFilter,
			sensorsFilter,
			zonesFilter,
		};

		const idSets = getLocationZoneSensorIdSets(locations);
		const previous = previousTreeRef.current;
		previousTreeRef.current = { idSets, locations };

		if (locations.length === 0) {
			const updates: {
				locationsFilter?: string[];
				sensorsFilter?: string[];
				zonesFilter?: string[];
			} = {};

			if (locationsFilter.length > 0) updates.locationsFilter = [];
			if (zonesFilter.length > 0) updates.zonesFilter = [];
			if (sensorsFilter.length > 0) updates.sensorsFilter = [];

			if (Object.keys(updates).length > 0) {
				hydrateEventsFilters(dispatch, updates);
			}
			return;
		}

		if (
			isPreReconcileLocationZoneSensorPlaceholder(filterIds, idSets) &&
			orgCode &&
			readStoredEventFilters(orgCode, scope)
		) {
			// Stored `[]` means "all selected" — do not expand to explicit IDs.
			// localStorage hydration runs in usePersistedEventsFilters (layout, before URL sync).
			return;
		}

		const reconciled = reconcileLocationZoneSensorFilters(
			filterIds,
			locations,
			idSets,
			previous,
		);

		const updates: {
			locationsFilter?: string[];
			sensorsFilter?: string[];
			zonesFilter?: string[];
		} = {};

		if (!sameIdSet(reconciled.locationsFilter, locationsFilter)) {
			updates.locationsFilter = reconciled.locationsFilter;
		}
		if (!sameIdSet(reconciled.zonesFilter, zonesFilter)) {
			updates.zonesFilter = reconciled.zonesFilter;
		}
		if (!sameIdSet(reconciled.sensorsFilter, sensorsFilter)) {
			updates.sensorsFilter = reconciled.sensorsFilter;
		}

		if (Object.keys(updates).length > 0) {
			hydrateEventsFilters(dispatch, updates);
		}
	}, [
		enabled,
		dispatch,
		isLoadingLocations,
		locations,
		locationsFilter,
		orgCode,
		sensorsFilter,
		scope,
		zonesFilter,
	]);
}
